import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api';
import { authService } from './authService';
import { authStorage } from '../utils/authStorage';

declare module 'axios' {
  interface AxiosRequestConfig {
    queryParams?: Record<string, any>;
    skipAuth?: boolean;
    skipSdkProductId?: boolean;
    _retry?: boolean;
  }
}

const DEFAULT_SDK_PRODUCT_ID = 10005;
const REFRESH_BEFORE_EXPIRE_MS = 2 * 60 * 1000;

let refreshPromise: Promise<string | null> | null = null;

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

const redirectToLogin = () => {
  authStorage.clear();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const refreshAccessToken = async (): Promise<string | null> => {
  const tokens = authStorage.get();
  if (!tokens?.refreshToken || tokens.refreshTokenExpireAt <= Date.now()) {
    redirectToLogin();
    return null;
  }

  if (!refreshPromise) {
    refreshPromise = authService
      .refresh(tokens.refreshToken)
      .then((newTokens) => newTokens.accessToken)
      .catch(() => {
        redirectToLogin();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const ensureFreshToken = async (): Promise<string | null> => {
  const tokens = authStorage.get();
  if (!tokens?.accessToken) {
    return null;
  }

  if (tokens.accessTokenExpireAt - Date.now() <= REFRESH_BEFORE_EXPIRE_MS) {
    return refreshAccessToken();
  }

  return tokens.accessToken;
};

const appendSdkProductId = (config: AxiosRequestConfig) => {
  if (config.skipSdkProductId) {
    return;
  }

  config.params = {
    ...config.params,
    sdkProductId: DEFAULT_SDK_PRODUCT_ID,
  };

  const method = (config.method || 'get').toLowerCase();
  if (config.data instanceof FormData) {
    if (!config.data.has('sdkProductId')) {
      config.data.append('sdkProductId', String(DEFAULT_SDK_PRODUCT_ID));
    }
    if (config.headers) {
      delete (config.headers as any)['Content-Type'];
    }
    return;
  }

  if (method === 'post' || method === 'put' || method === 'delete') {
    config.data = {
      ...(config.data || {}),
      sdkProductId: DEFAULT_SDK_PRODUCT_ID,
    };
  }
};

api.interceptors.request.use(async (config) => {
  if (config.queryParams) {
    config.params = {
      ...config.params,
      ...config.queryParams,
    };
    delete config.queryParams;
  }

  appendSdkProductId(config);

  if (!config.skipAuth) {
    const token = await ensureFreshToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data && data.status !== 200 && data.status !== undefined) {
      const error = new Error(data.message || '请求失败') as any;
      error.status = data.status;
      error.response = data;
      return Promise.reject(error);
    }
    return data;
  },
  async (error: AxiosError) => {
    const originalConfig = error.config;
    const status = error.response?.status || (error as any).status;

    if (status === 401 && originalConfig && !originalConfig._retry && !originalConfig.skipAuth) {
      originalConfig._retry = true;
      const token = await refreshAccessToken();
      if (token) {
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers.Authorization = `Bearer ${token}`;
        return api(originalConfig);
      }
    }

    return Promise.reject(error);
  }
);

export const apiCall = {
  request: async <T = any>(config: {
    url: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    queryParams?: Record<string, any>;
    bodyData?: any;
    formData?: FormData;
  }): Promise<T> => {
    const { url, method = 'GET', queryParams, bodyData, formData } = config;

    const requestConfig: AxiosRequestConfig = {
      url,
      method,
    };

    if (queryParams) {
      requestConfig.queryParams = queryParams;
    }

    if (formData) {
      requestConfig.data = formData;
    } else if (bodyData !== undefined) {
      requestConfig.data = bodyData;
    }

    return api(requestConfig) as Promise<T>;
  },

  get: <T = any>(url: string, queryParams?: Record<string, any>): Promise<T> => {
    return apiCall.request<T>({ url, method: 'GET', queryParams });
  },

  post: <T = any>(url: string, bodyData?: any, queryParams?: Record<string, any>): Promise<T> => {
    return apiCall.request<T>({ url, method: 'POST', bodyData, queryParams });
  },

  upload: <T = any>(url: string, formData: FormData): Promise<T> => {
    return apiCall.request<T>({ url, method: 'POST', formData });
  },
};
